/**
 * SEO Utility Functions for Twygs Website
 * Provides consistent SEO metadata generation across all pages
 * Works for both client-side and server-side rendering
 */

// Base URL configuration - supports both main site and newsletter
const BASE_URL = process.env.REACT_APP_API_GATEWAY_URL;

// Environment-based robots directive
const getRobotsDirective = () => {
  // Only allow indexing in production environment, consistent with robots.txt logic
  return process.env.NODE_ENV === 'production' ? 'index, follow' : 'noindex';
};

/**
 * Generate SEO metadata for different page types
 */
const generateSEOMetadata = {
  // Homepage SEO
  homepage: () => ({
    title: 'Twygs | A Platform for Quality Content',
    description:
      'Twygs is a content platform that empowers creators and viewers to share balanced perspectives, rewarding meaningful engagement over clicks.',
    canonical: BASE_URL,
    ogType: 'website',
    ogTitle: 'Twygs | A Platform for Quality Content',
    ogDescription:
      'Twygs is a content platform that empowers creators and viewers to share balanced perspectives, rewarding meaningful engagement over clicks.',
    ogImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    twitterCard: 'summary_large_image',
    twitterTitle: 'Twygs | A Platform for Quality Content',
    twitterDescription:
      'Twygs is a content platform that empowers creators and viewers to share balanced perspectives, rewarding meaningful engagement over clicks.',
    twitterImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    robots: getRobotsDirective(),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebSite',
      name: 'Twygs',
      url: BASE_URL,
      potentialAction: {
        '@type': 'SearchAction',
        target: {
          '@type': 'EntryPoint',
          urlTemplate: `${BASE_URL}/search?q={search_term_string}`
        },
        'query-input': 'required name=search_term_string'
      }
    }
  }),

  // Collection pages SEO
  collectionPage: (pageName, pageUrl, description) => ({
    title: `${pageName} | Discover Curated Ideas on Twygs`,
    description,
    canonical: `${BASE_URL}${pageUrl}`,
    ogType: 'website',
    ogTitle: `${pageName} | Discover Curated Ideas on Twygs`,
    ogDescription: description,
    ogImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    twitterCard: 'summary_large_image',
    twitterTitle: `${pageName} | Discover Curated Ideas on Twygs`,
    twitterDescription: description,
    twitterImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    robots: getRobotsDirective(),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: pageName,
      url: `${BASE_URL}${pageUrl}`,
      description: description,
      mainEntity: {
        '@type': 'ItemList',
        name: pageName,
        description: description
      }
    }
  }),

  // Search results SEO
  searchResults: (searchQuery) => ({
    title: `"${searchQuery}" | Find Thoughtful Stories and Authors on Twygs`,
    description: `Search Twygs for stories, authors, and topics that interest you. Discover thoughtful content and balanced perspectives across the platform.`,
    canonical: `${BASE_URL}/search?q=${encodeURIComponent(searchQuery)}`,
    ogType: 'website',
    ogTitle: `"${searchQuery}" | Find Thoughtful Stories and Authors on Twygs`,
    ogDescription: `Search Twygs for stories, authors, and topics that interest you. Discover thoughtful content and balanced perspectives across the platform.`,
    ogImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    twitterCard: 'summary',
    twitterTitle: `"${searchQuery}" | Find Thoughtful Stories and Authors on Twygs`,
    twitterDescription: `Search Twygs for stories, authors, and topics that interest you. Discover thoughtful content and balanced perspectives across the platform.`,
    twitterImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    robots: getRobotsDirective(),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'SearchResultsPage',
      name: 'Search',
      url: `${BASE_URL}/search`
    }
  }),

  // User profile SEO
  userProfile: (username, displayName, userData = null) => {
    // Validate username
    if (!username) {
      console.warn('Invalid username provided to userProfile SEO generator:', username);
      return generateSEOMetadata.homepage();
    }

    // Use provided userData or fallback to displayName/username
    const actualDisplayName = userData?.firstName || displayName || username;
    const profileImage =
      userData?.imgUrl ||
      'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg';

    return {
      title: `${actualDisplayName} | ${username} | Twygs`,
      description: `Visit ${username}'s profile on Twygs. Explore their latest content, articles, ideas, and contributions to a more mindful and informed online community.`,
      canonical: `${BASE_URL}/${username}`,
      ogType: 'profile',
      ogTitle: `${actualDisplayName} | ${username} | Twygs`,
      ogDescription: `Visit ${username}'s profile on Twygs. Explore their latest content, articles, ideas, and contributions to a more mindful and informed online community.`,
      ogImage: profileImage,
      twitterCard: 'summary',
      twitterTitle: `${actualDisplayName} | ${username} | Twygs`,
      twitterDescription: `Visit ${username}'s profile on Twygs. Explore their latest content, articles, ideas, and contributions to a more mindful and informed online community.`,
      twitterImage: profileImage,
      robots: getRobotsDirective(),
      schema: {
        '@context': 'https://schema.org',
        '@type': 'ProfilePage',
        name: username,
        url: `${BASE_URL}/${username}`,
        mainEntity: {
          '@type': 'Person',
          name: actualDisplayName,
          alternateName: username,
          url: `${BASE_URL}/${username}`,
          image: profileImage
        }
      }
    };
  },

  // Article SEO
  article: (article) => {
    // Validate article data
    if (!article || !article.id || !article.title) {
      console.warn('Invalid article data provided to SEO generator:', article);
      return generateSEOMetadata.homepage();
    }

    const articleUrl = `${BASE_URL}/articles/${article.alternativeId || article.id}`;
    const authorName = article.creator?.firstName || article.creator?.username || 'Anonymous';
    const authorUsername = article.creator?.username;

    // Handle problematic content by providing a safe description
    let articleDescription = article.description;
    if (!articleDescription || articleDescription.length < 10) {
      articleDescription = `Read ${authorName}'s article "${article.title}" on Twygs - a platform for thoughtful content and balanced perspectives.`;
    }

    // Ensure description is not too long for SEO
    if (articleDescription.length > 160) {
      articleDescription = articleDescription.substring(0, 157) + '...';
    }

    // Handle moderation warnings in title
    let seoTitle = article.title;
    if (article.moderation?.showWarning) {
      seoTitle = `${article.title} - Content Notice`;
    }

    return {
      title: `${seoTitle} | ${authorName} | Twygs`,
      description: articleDescription,
      canonical: articleUrl,
      ogType: 'article',
      ogTitle: `${seoTitle} | ${authorName} | Twygs`,
      ogDescription: articleDescription,
      ogImage:
        article.thumbnails?.medium ||
        article.cover ||
        'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
      twitterCard: 'summary_large_image',
      twitterTitle: `${seoTitle} | ${authorName} | Twygs`,
      twitterDescription: articleDescription,
      twitterImage:
        article.thumbnails?.medium ||
        article.cover ||
        'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
      robots: getRobotsDirective(),
      schema: {
        '@context': 'https://schema.org',
        '@type': 'Article',
        '@id': articleUrl,
        mainEntityOfPage: {
          '@type': 'WebPage',
          '@id': articleUrl
        },
        headline: seoTitle,
        description: articleDescription,
        author: {
          '@type': 'Person',
          name: authorName,
          url: authorUsername ? `${BASE_URL}/${authorUsername}` : undefined
        },
        publisher: {
          '@type': 'Organization',
          name: 'Twygs',
          logo: {
            '@type': 'ImageObject',
            url: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
            width: 400,
            height: 400
          }
        },
        datePublished: article.createdAt || article.publishedAt,
        dateModified: article.updatedAt || article.createdAt || article.publishedAt,
        image:
          article.thumbnails?.medium ||
          article.cover ||
          'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg'
      }
    };
  },

  // Static pages SEO
  staticPage: (pageName, pageUrl, description) => ({
    title: `${pageName} | Twygs`,
    description,
    canonical: `${BASE_URL}${pageUrl}`,
    ogType: 'website',
    ogTitle: `${pageName} | Twygs`,
    ogDescription: description,
    ogImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    twitterCard: 'summary',
    twitterTitle: `${pageName} | Twygs`,
    twitterDescription: description,
    twitterImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    robots: getRobotsDirective(),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: pageName,
      url: `${BASE_URL}${pageUrl}`,
      description
    }
  }),

  // Dynamic pages SEO (create, edit, manage)
  dynamicPage: (pageName, pageUrl, description) => ({
    title: `${pageName} | Twygs`,
    description,
    canonical: `${BASE_URL}${pageUrl}`,
    ogType: 'website',
    ogTitle: `${pageName} | Twygs`,
    ogDescription: description,
    ogImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    twitterCard: 'summary',
    twitterTitle: `${pageName} | Twygs`,
    twitterDescription: description,
    twitterImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    robots: getRobotsDirective(),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: pageName,
      url: `${BASE_URL}${pageUrl}`,
      description
    }
  }),

  // Request pages SEO
  requestPage: (pageName, pageUrl, description) => ({
    title: `${pageName} | Twygs`,
    description,
    canonical: `${BASE_URL}${pageUrl}`,
    ogType: 'website',
    ogTitle: `${pageName} | Twygs`,
    ogDescription: description,
    ogImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    twitterCard: 'summary',
    twitterTitle: `${pageName} | Twygs`,
    twitterDescription: description,
    twitterImage: 'https://pbs.twimg.com/profile_images/1778639402735915009/GC30UJWJ_400x400.jpg',
    robots: getRobotsDirective(),
    schema: {
      '@context': 'https://schema.org',
      '@type': 'WebPage',
      name: pageName,
      url: `${BASE_URL}${pageUrl}`,
      description
    }
  })
};

/**
 * Collection page configurations
 */
const collectionPages = {
  'recommended-topics': {
    name: 'Recommended Topics',
    url: '/recommended-topics',
    description:
      'Explore recommended topics on Twygs — discover balanced stories, trending ideas, and thoughtful discussions tailored to your interests.'
  },
  'breaking-news': {
    name: 'Breaking News',
    url: '/breaking-news',
    description:
      "Stay informed with breaking news on Twygs. Read credible, timely updates and deeper context behind today's most important stories."
  },
  'related-articles': {
    name: 'Related Articles',
    url: '/related-articles',
    description:
      'Find related articles on Twygs to explore different viewpoints, expand your understanding, and discover new voices on every topic.'
  },
  'featured-voices': {
    name: 'Featured Voices',
    url: '/featured-voices',
    description:
      "Meet Twygs' featured voices — creators and thinkers sharing meaningful stories that inspire reflection, curiosity, and balance."
  }
};

/**
 * Static page configurations
 */
const staticPages = {
  'privacy-policy': {
    name: 'Privacy Policy',
    url: '/privacy-policy',
    description:
      'Learn how Twygs protects your privacy and keeps your data secure. Read our Privacy Policy to understand how we collect, use, and safeguard your information.'
  },
  'terms-of-use': {
    name: 'Terms of Use',
    url: '/terms-of-use',
    description:
      "Review Twygs' Terms of Use outlining your rights, responsibilities, and platform guidelines for respectful, ethical participation."
  },
  'cookie-policy': {
    name: 'Cookie Policy',
    url: '/cookie-policy',
    description:
      'Discover how Twygs uses cookies to enhance your experience, analyze performance, and personalize content. Manage your cookie preferences anytime.'
  },
  roadmap: {
    name: 'Product Roadmap',
    url: '/roadmap',
    description:
      'Explore the Twygs product roadmap and see upcoming features, improvements, and ideas driving a better, more mindful content experience.'
  }
};

/**
 * Dynamic page configurations
 */
const dynamicPages = {
  'article/create': {
    name: 'Create Article',
    url: '/article/create',
    description:
      'Create a new article on Twygs. Share your perspective, inspire conversation, and reach an audience that values meaningful content.'
  },
  'article/edit': {
    name: 'Edit Article',
    url: '/article/edit',
    description:
      'Edit your Twygs article to refine your writing, update ideas, or enhance your content before publishing to your readers.'
  },
  'article/manage': {
    name: 'Manage Articles',
    url: '/article/manage',
    description:
      'Manage your published and draft articles on Twygs. Track performance, edit posts, and engage with your growing audience.'
  },
  'request/create': {
    name: 'Create Request',
    url: '/request/create',
    description:
      'Submit a new content request on Twygs. Invite contributors to share diverse perspectives and contribute to stories that matter.'
  },
  'request/edit': {
    name: 'Edit Request',
    url: '/request/{id}/edit',
    description:
      'Edit your Twygs content request. Update details, adjust deadlines, or refine your topic to attract the right contributors.'
  },
  'request/submissions': {
    name: 'View Submissions',
    url: '/request/{id}/submissions',
    description:
      'Review all submissions for your Twygs content request. Discover unique takes, thoughtful writing, and creator insights.'
  },
  'request/detail': {
    name: 'Request Details',
    url: '/request/{id}/detail',
    description:
      'View full details of your Twygs request, including submissions, participants, and updates related to your topic.'
  },
  'account-settings': {
    name: 'Account Settings',
    url: '/account-settings',
    description:
      'Manage your Twygs account settings, profile, and privacy options. Personalize your experience and stay in control of your data.'
  },
  'account-settings/two-factor-authentication': {
    name: 'Two-Factor Authentication',
    url: '/account-settings/two-factor-authentication',
    description:
      'Protect your Twygs account with two-factor authentication. Add an extra layer of security to keep your information safe.'
  },
  notifications: {
    name: 'Notifications',
    url: '/notifications',
    description:
      'View your Twygs notifications and stay updated on comments, likes, followers, and content activity that matters to you.'
  }
};

/**
 * Generate request-specific SEO for dynamic request pages
 */
const generateRequestSEOMetadata = (requestId, action = 'detail') => {
  const baseUrl = `${BASE_URL}/request/${requestId}`;

  const configs = {
    edit: {
      name: 'Edit Request',
      url: `${baseUrl}/edit`,
      description:
        'Edit your Twygs content request. Update details, adjust deadlines, or refine your topic to attract the right contributors.'
    },
    submissions: {
      name: 'View Submissions',
      url: `${baseUrl}/submissions`,
      description:
        'Review all submissions for your Twygs content request. Discover unique takes, thoughtful writing, and creator insights.'
    },
    detail: {
      name: 'Request Details',
      url: `${baseUrl}/detail`,
      description:
        'View full details of your Twygs request, including submissions, participants, and updates related to your topic.'
    }
  };

  const config = configs[action] || configs.detail;
  return generateSEOMetadata.dynamicPage(config.name, config.url, config.description);
};

// CommonJS export support for server-side usage
if (typeof module !== 'undefined' && module.exports) {
  module.exports = {
    generateSEOMetadata,
    collectionPages,
    staticPages,
    dynamicPages,
    generateRequestSEOMetadata,
    getRobotsDirective
  };
}
